import {
    createArrayComparator,
    createEqualityChecker,
    createNamedEqualityChecker,
    createPrimitiveValueComparator,
    isArrayNotEmpty,
    type ReadonlyDate,
} from '../../utils'

import {
    arePermissionArrayEquals,
    type CybusUser,
    type CybusUserAuthenticationMethod,
    type EditableCybusPermission,
    isPermissionValid,
    type UserForm,
} from '..'
import type { Loadable } from '../Loadable'

export type ClientRegistryRequest = Pick<CybusUser, 'username' | 'roles'> &
    Readonly<{
        description: string
        timestamp: ReadonlyDate
        authenticationMethod: CybusUserAuthenticationMethod
        permissions: readonly EditableCybusPermission[]
    }>

export type ClientRegistryAuthorizationRequest = Pick<CybusUser, 'username' | 'mqttPublishPrefix' | 'authenticationMethods'> &
    Pick<UserForm, 'roles' | 'permissions'>

export type ClientRegistryRequestForm = Pick<CybusUser, 'username' | 'mqttPublishPrefix' | 'authenticationMethods'> &
    UserForm &
    Readonly<{ request: ClientRegistryRequest }>

export type EditableClientRegistryRequestForm = Pick<ClientRegistryRequestForm, 'mqttPublishPrefix' | 'authenticationMethods' | 'roles' | 'permissions'>

export type ClientRegistryData = Readonly<{ unlockedUntil: ReadonlyDate | null, requests: readonly ClientRegistryRequest[] }>

export type ClientRegistry = Readonly<{ locked: boolean | null, data: Loadable<ClientRegistryData> }>

export const areClientRegistryRequestFormsEqual = createNamedEqualityChecker<EditableClientRegistryRequestForm>(
    { mqttPublishPrefix: null, roles: createArrayComparator(), permissions: arePermissionArrayEquals, authenticationMethods: createArrayComparator() },
    'areClientRegistryRequestFormsEqual'
)

export const isClientRegistryRequestCreatable = ({ permissions, authenticationMethods }: ClientRegistryRequestForm): boolean =>
    isArrayNotEmpty(authenticationMethods) && permissions.every(isPermissionValid)

const areClientRegistryRequestEquals = createEqualityChecker<ClientRegistryRequest>({
    username: null,
    description: null,
    timestamp: createPrimitiveValueComparator((t) => t.getTime()),
    authenticationMethod: null,
    roles: createArrayComparator(),
    permissions: arePermissionArrayEquals,
})

export const areClientRegistryDataEquals = createNamedEqualityChecker<ClientRegistryData>(
    { unlockedUntil: createPrimitiveValueComparator((t) => t?.getTime()), requests: createArrayComparator(areClientRegistryRequestEquals) },
    'areClientRegistryDataEquals'
)
